"use client"

import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { useDialogModal } from "@/hook/handle-modal/hooks/actions"
import type { ColumnDef } from "@tanstack/react-table"
import { FileText, History } from "lucide-react"
import { useEffect, useRef } from "react"
import { GnreHistoryModal, NffHistoryModal } from "../modal"
import { DataTableColumnHeader } from "./data-table-column-header"
import { DataTableRowActions } from "./data-table-row-actions"
import { StateFlagCell } from "./state-flag-cell"
import { PayedSwitchCell } from "./toolbar-options/payed-switch-cell"
import { useSelectedItems } from "../../context/selected-items-context"

export function getColumns(): ColumnDef<any>[] {
  const { handleModal } = useDialogModal()
  const { selectedItemKeys, setSelectedItemKeys } = useSelectedItems()

  return [
    {
      id: "select",
      header: ({ table }) => {
        const pageRows = table.getRowModel().rows
        const pageKeys = pageRows.map((row) => row.original?.id ?? row.original?.nfe_key)
        const allPageSelected = pageKeys.every((key) => selectedItemKeys.has(key)) && pageKeys.length > 0
        const somePageSelected = pageKeys.some((key) => selectedItemKeys.has(key))
        const checkboxRef = useRef<HTMLButtonElement>(null)

        useEffect(() => {
          if (checkboxRef.current) {
            const input = checkboxRef.current.querySelector('input[type="checkbox"]') as HTMLInputElement | null
            if (input) {
              input.indeterminate = !allPageSelected && somePageSelected
            }
          }
        }, [allPageSelected, somePageSelected])

        return (
          <div className="w-full flex items-center justify-center justify-items-center pr-3">
            <Checkbox
              ref={checkboxRef}
              checked={allPageSelected}
              onCheckedChange={(value) => {
                const newKeys = new Set(selectedItemKeys)

                if (value) {
                  pageKeys.forEach((key) => newKeys.add(key))
                } else {
                  pageKeys.forEach((key) => newKeys.delete(key))
                }

                setSelectedItemKeys(newKeys)

                table.toggleAllPageRowsSelected(!!value)

                console.log("Total de itens selecionados:", newKeys.size)
              }}
              aria-label="Select all on page"
            />
          </div>
        )
      },
      cell: (cellContext) => {
        const row = cellContext.row
        const key = row.original?.id ?? row.original?.nfe_key
        return (
          <Checkbox
            checked={selectedItemKeys.has(key)}
            onCheckedChange={(checked) => {
              const newKeys = new Set(selectedItemKeys)
              if (checked) {
                newKeys.add(key)
              } else {
                newKeys.delete(key)
              }
              setSelectedItemKeys(newKeys)

              row.toggleSelected(!!checked)

              console.log("Total de itens selecionados:", newKeys.size)
            }}
            aria-label="Select row"
          />
        )
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "invoice_number",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Número da Nota" />,
    },
    {
      accessorKey: "emission_date",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Data de Emissão" />,
      cell: ({ row }) => {
        const dateStr = row.getValue("emission_date")
        const date = new Date(dateStr as string)
        const day = String(date.getUTCDate()).padStart(2, "0")
        const month = String(date.getUTCMonth() + 1).padStart(2, "0")
        const year = date.getUTCFullYear()
        const formatted = `${day}/${month}/${year}`
        return <div>{formatted}</div>
      },
    },
    {
      accessorKey: "cnpj",
      header: ({ column }) => <DataTableColumnHeader column={column} title="CNPJ" />,
    },
    {
      accessorKey: "state",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Estado" />,
      cell: ({ row }) => {
        const state = row.getValue("state") as string
        return <StateFlagCell uf={state} />
      },
    },
    {
      accessorKey: "nfe_key",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Chave NFe" />,
    },
    // {
    //   accessorKey: "status",
    //   header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    // },
    {
      id: "gnre_generated",
      header: "Lotes GNRE",
      cell: ({ row }) => {
        const receivedGnre = row.original?.received_gnre
        if (Array.isArray(receivedGnre) && receivedGnre.length > 0) {
          const batches = receivedGnre.slice(0, 2).map(gnre => gnre.batch ? gnre.batch.slice(0, 3) : "-")
          const more = receivedGnre.length - 2
          const displayText = batches.join(', ') + (more > 0 ? ` +${more}` : '')
          return (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 whitespace-nowrap"
              title="Clique para ver o histórico completo do GNRE"
              onClick={() => {
                handleModal({
                  isOpen: true,
                  title: "Histórico de GNRE",
                  description: `Nota Fiscal: ${row.original?.invoice_number}`,
                  element: <GnreHistoryModal gnreHistory={receivedGnre} />,
                })
              }}
            >
              <History className="w-3 h-3" />
              <span>{displayText}</span>
            </button>
          )
        }
        return (
          <div className="flex items-center gap-2 justify-center w-full">
            <Badge variant="destructive" className="bg-red-50 text-red-700 border-red-200">
              Não
            </Badge>
          </div>
        )
      },
    },
    {
      id: "nff_generated",
      header: "NFFs Geradas",
      cell: ({ row }) => {
        const receivedGnre = row.original?.received_gnre
        // Filtra apenas os que tem NFF gerada (generated_nff: true e nff preenchido)
        const nffsGeradas = Array.isArray(receivedGnre)
          ? receivedGnre.filter(gnre => gnre.generated_nff && gnre.nff)
          : []

        if (nffsGeradas.length > 0) {
          const nffs = nffsGeradas.slice(0, 2).map(item => item.nff ? item.nff.slice(0, 6) : "-")
          const more = nffsGeradas.length - 2
          const displayText = nffs.join(', ') + (more > 0 ? ` +${more}` : '')
          return (
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 text-xs font-medium rounded-md hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 whitespace-nowrap"
              title="Clique para ver o histórico completo de NFFs"
              onClick={() => {
                handleModal({
                  isOpen: true,
                  title: "Histórico de NFF",
                  description: `Nota Fiscal: ${row.original?.invoice_number}`,
                  element: <NffHistoryModal nffHistory={nffsGeradas} />,
                })
              }}
            >
              <FileText className="w-3 h-3" />
              <span>{displayText}</span>
            </button>
          )
        }
        return (
          <div className="flex items-center gap-2 justify-center w-full">
            <Badge variant="destructive" className="bg-red-50 text-red-700 border-red-200">
              Não
            </Badge>
          </div>
        )
      },
    },
    {
      id: "toogle_payed",
      header: "Foi Pago?",
      cell: ({ row }) => <PayedSwitchCell row={row} />,
    },
    {
      id: "actions",
      header: "Visualizar Composição",
      cell: ({ row }) => <DataTableRowActions row={row} />,
    },
  ]
}
